import type { StatusTone } from '@noor/ui';
import type { Transaction } from '../model/schema';

/**
 * Transaction type / status lookups — the numbers and slugs are the backend
 * contract (cpanel `assets/data/transaction-types`). Cpanel painted each type
 * with its own hex; the design system only has semantic status tones, so each
 * type maps to the nearest tone (money in → success, debits → danger, holds
 * and tips → pending, order flow → active). Labels resolve through i18n
 * (`reports:transactions.types.<number>` / `.statuses.<number>`).
 */
export interface TransactionTypeDef {
  number: number;
  id: string;
  tone: StatusTone;
}

export const TRANSACTION_TYPES: readonly TransactionTypeDef[] = [
  { number: 1, id: 'TOP_UP', tone: 'success' },
  { number: 2, id: 'WITHDRAW', tone: 'danger' },
  { number: 3, id: 'ORDER_PROFIT', tone: 'active' },
  { number: 4, id: 'TIP', tone: 'pending' },
  { number: 5, id: 'BONUS', tone: 'success' },
  { number: 6, id: 'REFUND', tone: 'active' },
  { number: 7, id: 'ORDER_DEBT', tone: 'danger' },
  { number: 8, id: 'SCOOTER', tone: 'neutral' },
  { number: 9, id: 'FOULS', tone: 'danger' },
  { number: 10, id: 'HOLD', tone: 'pending' },
  { number: 11, id: 'IBOX', tone: 'success' },
  { number: 12, id: 'COMMISSION_BUSINESS', tone: 'active' },
  { number: 13, id: 'TAX', tone: 'danger' },
  { number: 14, id: 'COMPENSATION', tone: 'pending' },
  { number: 15, id: 'TAX_REFUND', tone: 'active' },
  { number: 16, id: 'DEPOSIT', tone: 'active' },
  { number: 17, id: 'DEPOSIT_EXPENSE', tone: 'pending' },
  { number: 18, id: 'WITHDRAW_FROM_DEPOSIT', tone: 'danger' },
  { number: 19, id: 'TERMOSUMKA_PAYMENT', tone: 'success' },
  { number: 20, id: 'HELMET_PAYMENT', tone: 'active' },
];

export interface TransactionStatusDef {
  number: number;
  id: string;
  tone: StatusTone;
}

export const TRANSACTION_STATUSES: readonly TransactionStatusDef[] = [
  { number: 1, id: 'SUCCEEDED', tone: 'success' },
  { number: 2, id: 'FAILED', tone: 'active' },
  { number: 3, id: 'CANCELLED', tone: 'danger' },
  { number: 4, id: 'PENDING', tone: 'info' },
];

/** Type 11 (IBOX) rows carry no balance snapshot, so the cell hides it. */
export const IBOX_TRANSACTION_TYPE = 11;

export function transactionTypeDef(
  type: number | string | null | undefined,
): TransactionTypeDef | undefined {
  return TRANSACTION_TYPES.find((item) => item.number === Number(type));
}

export function transactionStatusDef(
  status: number | string | null | undefined,
): TransactionStatusDef | undefined {
  return TRANSACTION_STATUSES.find((item) => item.number === Number(status));
}

/** Bonus (5) and fine (9) rows still marked succeeded (1) can be cancelled. */
export function canCancelTransaction(tx: Pick<Transaction, 'type' | 'status'>): boolean {
  return (tx.type === 9 || tx.type === 5) && tx.status === 1;
}

/**
 * Who a transaction belongs to: the courier, else the vendor, else the client
 * (cpanel's nested ternary). `kind` keys the localized role suffix.
 */
export interface TransactionOwner {
  kind: 'courier' | 'vendor' | 'client';
  name: string;
  id: string | number;
}

/* eslint-disable @typescript-eslint/prefer-nullish-coalescing --
   Ported verbatim from cpanel's phone cell: `||` lets an empty name fall to `-`. */
export function transactionOwner(tx: Transaction): TransactionOwner | undefined {
  if (tx.courier_id) {
    return {
      kind: 'courier',
      name: `${tx.courier_first_name || '-'} ${tx.courier_last_name || '-'}`,
      id: tx.courier_id,
    };
  }
  if (tx.vendor_id) {
    return { kind: 'vendor', name: tx.vendor_name || '-', id: tx.vendor_id };
  }
  if (tx.client_id) {
    return {
      kind: 'client',
      name: `${tx.client_first_name || '-'} ${tx.client_last_name || '-'}`,
      id: tx.client_id,
    };
  }
  return undefined;
}
/* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
